<template>
  <div v-if="!hideWhenEmpty || receipts.length" class="sr">
    <h5 class="sr__title">{{ title }}</h5>
    <p v-if="loading" class="sr__muted">Henter kvitteringene…</p>
    <p v-else-if="error" class="sr__error" role="alert">{{ error }}</p>
    <p v-else-if="!receipts.length" class="sr__muted">Ingen kvitteringer ennå. Den første kommer når kortet trekkes.</p>
    <table v-else class="sr__table">
      <thead>
        <tr>
          <th scope="col">Periode</th>
          <th scope="col">Trukket</th>
          <th scope="col" class="sr__num">Beløp inkl. mva</th>
          <th scope="col"><span class="visually-hidden">Bilag</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in receipts" :key="r.id">
          <td data-label="Periode">{{ period(r) }}</td>
          <td data-label="Trukket">{{ issued(r.issuedAt) }}</td>
          <td data-label="Beløp inkl. mva" class="sr__num">{{ kr(r.grossMinor) }}</td>
          <td class="sr__actions">
            <button v-if="r.hasPdf" type="button" class="sr__link" :disabled="busy === r.id" @click="download(r)"
                    :aria-label="'Last ned kvitteringen for ' + period(r)">
              <font-awesome-icon :icon="['fas', 'file-pdf']" /> {{ busy === r.id ? 'Henter…' : 'PDF' }}
            </button>
            <span v-else class="sr__muted" title="PDF-en kunne ikke lages da kortet ble trukket">–</span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="sr__muted sr__note">
      Kvitteringer fra før de ble lagret her, ligger bare i e-posten («CAGISTA-kvittering-….pdf»).
    </p>
  </div>
</template>

<script>
import SubscriptionService from "@/services/site/SubscriptionService";
import VivaSettlementFormat from "@/util/VivaSettlementFormat";

/**
 * CAGISTA's receipts for the licence, newest first, each with the PDF that was e-mailed — the bilag for a cost the
 * merchant books. On «Abonnement og betaling» for the owner, and on «Regnskapsbilag» for the accountant, where it hides
 * itself when there is nothing to show (a merchant without a CAGISTA subscription has no receipts). The server keeps
 * them from 2026-10-25 (CagistaServices billing/SubscriptionReceipts).
 */
export default {
  name: "SubscriptionReceipts",
  props: {
    title: { type: String, default: "Kvitteringer fra CAGISTA" },
    hideWhenEmpty: { type: Boolean, default: false }
  },
  data() {
    return { receipts: [], loading: true, error: "", busy: "" };
  },
  mounted() {
    SubscriptionService.getReceipts().then(
      (response) => {
        this.loading = false;
        this.receipts = (response.data && response.data.receipts) || [];
      },
      (error) => {
        this.loading = false;
        const body = error && error.response ? error.response.data : null;
        this.error = (body && body.error) || "Kvitteringene kunne ikke hentes. Prøv igjen.";
      }
    );
  },
  methods: {
    period(r) {
      return r.periodFrom && r.periodTo ? VivaSettlementFormat.period(r.periodFrom, r.periodTo) : r.reference;
    },
    issued(iso) {
      if (!iso) { return "–"; }
      return new Intl.DateTimeFormat("nb-NO", { timeZone: "Europe/Oslo", day: "2-digit", month: "2-digit", year: "numeric" })
        .format(new Date(iso));
    },
    kr(ore) {
      return VivaSettlementFormat.kr(ore);
    },
    download(r) {
      this.busy = r.id;
      SubscriptionService.getReceiptPdf(r.id).then(
        (response) => {
          this.busy = "";
          const data = response.data || {};
          const binary = atob(data.pdf || "");
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          VivaSettlementFormat.download(bytes, data.fileName || "CAGISTA-kvittering.pdf", "application/pdf");
        },
        (error) => {
          this.busy = "";
          const body = error && error.response ? error.response.data : null;
          this.error = (body && body.error) || "Kvitteringen kunne ikke hentes. Prøv igjen.";
        }
      );
    }
  }
};
</script>

<style scoped>
.sr__title { font-size: 1rem; font-weight: 600; margin: 0 0 8px; }
.sr__muted { color: var(--bo-muted); font-size: 0.9rem; margin: 0; }
.sr__note { margin-top: 8px; }
.sr__error { color: var(--bo-alert-ink, #b00020); margin: 0; }
.sr__table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
.sr__table th { text-align: left; font-weight: 600; color: var(--bo-ink-2); border-bottom: 1px solid var(--bo-line); padding: 6px 8px; }
.sr__table td { border-bottom: 1px solid var(--bo-line); padding: 8px; }
.sr__num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.sr__actions { text-align: right; white-space: nowrap; }
.sr__link {
  border: 0; background: none; color: var(--bo-brand-ink); font-weight: 600; padding: 2px 6px; border-radius: 6px;
  cursor: pointer; min-height: var(--bo-touch-target, 44px);
}
.sr__link:hover { background: var(--bo-brand-tint); }
.sr__link:disabled { opacity: 0.6; cursor: default; }
@media (max-width: 600px) {
  .sr__table thead { display: none; }
  .sr__table tr {
    display: grid; grid-template-columns: 1fr auto; grid-template-areas: "period amount" "issued pdf";
    gap: 2px 12px; padding: 8px 0; border-bottom: 1px solid var(--bo-line); align-items: center;
  }
  .sr__table td { border: 0; padding: 0; }
  .sr__table td[data-label="Periode"] { grid-area: period; }
  .sr__table td[data-label="Beløp inkl. mva"] { grid-area: amount; }
  .sr__table td[data-label="Trukket"] { grid-area: issued; color: var(--bo-muted); font-size: 0.85rem; }
  .sr__table td.sr__actions { grid-area: pdf; }
}
</style>
